import {
  Component,
  ElementRef,
  inject,
  input,
  output,
  computed,
  linkedSignal,
  viewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  WorkflowNode,
  WorkflowNodeDefinition,
  WorkflowNodeType,
  WorkflowNodeRuleType,
  WorkflowNodeActionType,
  WorkflowNodeActionResponseType,
  DurationParts,
  parseDuration,
  formatDuration,
  isZeroDuration,
  WORKFLOW_EMAIL_TEMPLATES,
  WORKFLOW_TRIGGER_TYPES,
  extractReferencedMetadataKeys,
  needsMetadataDeclaration,
  nodeTypeIconFor,
  nodeTypeLabelFor
} from '@formrails/workflow-designer/data-access';
import { RequiredMetadataEditorComponent } from '../required-metadata-editor/required-metadata-editor.component';
import { ExpressionEditorComponent } from '../expression-editor/expression-editor.component';
import { PlaceholderPickerComponent } from '../placeholder-picker/placeholder-picker.component';
import { ResponseTemplateBuilderComponent } from '../response-template-builder/response-template-builder.component';

/**
 * Properties panel for the selected workflow node. The set of editable fields
 * is driven by the node's type and sub-type, mirroring how
 * WorkflowNodeDefinition is interpreted in Osstre.Domain:
 * - Trigger:            expression (event type)
 * - Rule/Condition:     expression + successorFalse branch
 * - Rule/WaitForTrigger: expression
 * - Rule/TimeDelay:     delay
 * - Action/SendEmail:   emailTemplateId, payload
 * - Action/Webhook:     url, method, payload, responseType, responseTemplate
 */
@Component({
  selector: 'app-workflow-node-properties',
  standalone: true,
  imports: [
    FormsModule,
    RequiredMetadataEditorComponent,
    ExpressionEditorComponent,
    PlaceholderPickerComponent,
    ResponseTemplateBuilderComponent
  ],
  templateUrl: './workflow-node-properties.component.html',
  styleUrl: './workflow-node-properties.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WorkflowNodePropertiesComponent {
  private readonly emailTemplates = inject(WORKFLOW_EMAIL_TEMPLATES);
  private readonly hostTriggerTypes = inject(WORKFLOW_TRIGGER_TYPES);

  readonly node = input<WorkflowNode | null>(null);
  /** All nodes, used to populate successor dropdowns. */
  readonly allNodes = input<WorkflowNode[]>([]);

  readonly nodeChange = output<WorkflowNode>();
  readonly deleteNode = output<string>();

  /** Trigger types from the host app (empty when none registered). */
  readonly triggerTypes = computed(() => this.hostTriggerTypes());
  /** Email templates from the host app. */
  readonly availableEmailTemplates = computed(() => this.emailTemplates());

  /** Textareas wired up for cursor-aware placeholder insertion. */
  readonly payloadTextarea = viewChild<ElementRef<HTMLTextAreaElement>>('payloadTa');
  readonly urlInput = viewChild<ElementRef<HTMLInputElement>>('urlInput');

  /** Local editable copy that resets whenever the selected node changes. */
  readonly draft = linkedSignal<WorkflowNode | null>(() => this.node());

  // Enum handles for the template
  readonly WorkflowNodeType = WorkflowNodeType;
  readonly ruleTypes = Object.values(WorkflowNodeRuleType);
  readonly actionTypes = Object.values(WorkflowNodeActionType);
  readonly responseTypes = Object.values(WorkflowNodeActionResponseType);
  readonly httpMethods = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as const;

  /** Names the sub-type being edited: "Time Delay", not the bare "Rule". */
  readonly typeLabel = computed(() => {
    const node = this.draft();
    return node ? nodeTypeLabelFor(node) : '';
  });

  /** The palette icon for what is being edited, sub-type first. */
  readonly typeIcon = computed(() => {
    const node = this.draft();
    return node ? nodeTypeIconFor(node) : '';
  });

  // Type/sub-type flags
  readonly isTrigger = computed(() => this.draft()?.type === WorkflowNodeType.Trigger);
  readonly isCondition = computed(
    () =>
      this.draft()?.type === WorkflowNodeType.Rule &&
      this.draft()?.ruleType === WorkflowNodeRuleType.Condition
  );
  readonly isWaitForTrigger = computed(
    () =>
      this.draft()?.type === WorkflowNodeType.Rule &&
      this.draft()?.ruleType === WorkflowNodeRuleType.WaitForTrigger
  );
  readonly isTimeDelay = computed(
    () =>
      this.draft()?.type === WorkflowNodeType.Rule &&
      this.draft()?.ruleType === WorkflowNodeRuleType.TimeDelay
  );
  readonly isSendEmail = computed(
    () =>
      this.draft()?.type === WorkflowNodeType.Action &&
      this.draft()?.actionType === WorkflowNodeActionType.SendEmail
  );
  readonly isWebhook = computed(
    () =>
      this.draft()?.type === WorkflowNodeType.Action &&
      this.draft()?.actionType === WorkflowNodeActionType.Webhook
  );
  readonly usesExpression = computed(
    () => this.isTrigger() || this.isCondition() || this.isWaitForTrigger()
  );

  /** Other nodes that can be targeted by a successor edge. */
  readonly successorOptions = computed(() =>
    this.allNodes().filter(n => n.id !== this.draft()?.id)
  );

  /** Merge a partial change into the draft node and emit it. */
  patch(change: Partial<WorkflowNode>): void {
    const current = this.draft();
    if (!current) return;
    const updated = { ...current, ...change };
    this.draft.set(updated);
    this.nodeChange.emit(updated);
  }

  /** Merge a partial change into the node's definition and emit. */
  patchDefinition(change: Partial<WorkflowNodeDefinition>): void {
    const current = this.draft();
    if (!current) return;
    this.patch({ definition: { ...current.definition, ...change } });
  }

  /** Decomposed view of `definition.delay` for the TimeDelay picker. */
  readonly delayParts = computed<DurationParts>(() =>
    parseDuration(this.draft()?.definition.delay)
  );

  /** Update a single component of the delay duration. */
  patchDelayPart(part: keyof DurationParts, value: number | string): void {
    const next: DurationParts = {
      ...this.delayParts(),
      [part]: Math.max(0, Math.floor(Number(value) || 0))
    };
    const delay = isZeroDuration(next) ? undefined : formatDuration(next);
    this.patchDefinition({ delay });
  }

  /** Insert a `{{token}}` into the payload textarea at the cursor. */
  insertIntoPayload(token: string): void {
    const ta = this.payloadTextarea()?.nativeElement;
    if (!ta) {
      this.patchDefinition({ payload: (this.draft()?.definition.payload ?? '') + token });
      return;
    }
    const next = insertAtCursor(ta, token);
    this.patchDefinition({ payload: next });
  }

  /** Insert a `{{token}}` into the URL input at the cursor. */
  insertIntoUrl(token: string): void {
    const el = this.urlInput()?.nativeElement;
    if (!el) {
      this.patchDefinition({ url: (this.draft()?.definition.url ?? '') + token });
      return;
    }
    const next = insertAtCursor(el, token);
    this.patchDefinition({ url: next });
  }

  /** True when Webhook ResponseTemplate fields should be visible. */
  readonly showResponseTemplate = computed(() => {
    const rt = this.draft()?.definition.responseType;
    return rt !== undefined && rt !== WorkflowNodeActionResponseType.None;
  });

  /**
   * Metadata keys referenced by the node's templated fields but not yet
   * declared in `requiredMetadata`. Surfaced as add-suggestions because the
   * runtime gate-checks declared keys before executing the node.
   */
  readonly suggestedMetadata = computed<string[]>(() => {
    const node = this.draft();
    if (!node) return [];
    const declared = new Set(node.requiredMetadata);
    return extractReferencedMetadataKeys(node.definition).filter(
      k => !declared.has(k) && needsMetadataDeclaration(k)
    );
  });

  acceptSuggestedMetadata(key: string): void {
    const current = this.draft();
    if (!current) return;
    if (current.requiredMetadata.includes(key)) return;
    this.patch({ requiredMetadata: [...current.requiredMetadata, key] });
  }

  acceptAllSuggestedMetadata(): void {
    const current = this.draft();
    if (!current) return;
    const additions = this.suggestedMetadata().filter(k => !current.requiredMetadata.includes(k));
    if (additions.length === 0) return;
    this.patch({ requiredMetadata: [...current.requiredMetadata, ...additions] });
  }

  onDelete(): void {
    const current = this.draft();
    if (current) this.deleteNode.emit(current.id);
  }
}

function insertAtCursor(el: HTMLInputElement | HTMLTextAreaElement, snippet: string): string {
  const start = el.selectionStart ?? el.value.length;
  const end = el.selectionEnd ?? start;
  const next = el.value.slice(0, start) + snippet + el.value.slice(end);
  queueMicrotask(() => {
    el.focus();
    const pos = start + snippet.length;
    el.setSelectionRange(pos, pos);
  });
  return next;
}
